import { collection, addDoc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { db, auth } from '../../firebase'; // Adjust this path to your Firebase config

// Define the structure for a log entry
export interface LogEntry {
  id?: string;                 // Auto-generated by Firestore (optional on creation)
  timestamp: Timestamp;         // Firestore Timestamp - ALWAYS REQUIRED (written server-side for accuracy)
  level: 'INFO' | 'WARN' | 'ERROR' | 'DEBUG' | 'CRITICAL'; // Log severity - ALWAYS REQUIRED
  message: string;              // Main log message - ALWAYS REQUIRED
  
  // ---- Application Context ----
  source?: string;             // Logical source, e.g., 'AuthService', 'EmailService', 'PaymentModule.ProcessPayment', 'Component.UserProfile'
  module?: string;               // Broader module/area of the app, e.g., 'Authentication', 'UserManagement', 'Email', 'Jobs'
  functionName?: string;         // Specific function/method where the log originated, e.g., 'handleLogin', 'sendWelcomeEmail'
  
  // ---- User Context (Optional but highly recommended for user-facing apps) ----
  userId?: string;               // ID of the user performing the action (if applicable)
  userEmail?: string;            // Email of the user (if applicable and privacy allows)
  sessionId?: string;            // User's session ID (if applicable)
  clientIp?: string;             // Client's IP address (if applicable and privacy/GDPR allows)

  // ---- Action/Event Context (Optional) ----
  action?: string;               // Specific action being logged, e.g., 'LOGIN_SUCCESS', 'EMAIL_SENT', 'DB_QUERY_FAILED', 'USER_PROFILE_UPDATED'
  entityType?: string;           // Type of entity involved, e.g., 'User', 'Job', 'Invoice', 'Email'
  entityId?: string | number;    // ID of the entity involved
  
  // ---- Technical Details (Especially for errors/debugging) ----
  errorCode?: string;            // Application-specific error code
  stackTrace?: string;           // Stack trace for errors (especially for ERROR/CRITICAL)
  requestDetails?: {            // Details about an HTTP request (if relevant)
    method?: string;
    url?: string;
    params?: Record<string, any>;
    body?: Record<string, any>; // Be cautious with sensitive data
  };
  responseDetails?: {           // Details about an HTTP response (if relevant)
    statusCode?: number;
    body?: Record<string, any>; // Be cautious with sensitive data
  };
  durationMs?: number;           // Duration of an operation in milliseconds
  
  // ---- Custom Data ----
  additionalData?: Record<string, any>; // For any other structured data relevant to the log
}

export type LogLevel = LogEntry['level'];

// Helper to get current user info (simplified)
const getCurrentUserInfo = () => {
  const user = auth.currentUser;
  if (user) {
    return { userId: user.uid, userEmail: user.email || undefined };
  }
  return {};
};

const log = async (
  level: LogLevel,
  message: string,
  details?: Partial<Omit<LogEntry, 'id' | 'timestamp' | 'level' | 'message'>>
) => {
  try {
    const logEntryData: Omit<LogEntry, 'id' | 'timestamp'> & { timestamp: any } = {
      level,
      message,
      ...getCurrentUserInfo(), // Add user info if available
      ...details, // Spread any additional details
      timestamp: serverTimestamp(), // Let Firestore set the server-side timestamp
    };

    await addDoc(collection(db, 'logs'), logEntryData);
    
    // Optional: For DEBUG level, also log to console during development
    if (level === 'DEBUG' && process.env.NODE_ENV === 'development') {
      console.debug(`[${new Date().toISOString()}] [${level}] ${details?.source ? '['+details.source+']' : ''}`, message, details || '');
    }

  } catch (error) {
    console.error("Failed to write log to Firestore:", error);
    // Fallback logging if Firestore fails (e.g., console log the original intended log)
    console.error(`FALLBACK LOG [${level}]: ${message}`, details);
  }
};

// Convenience functions for each level
export const logger = {
  debug: (message: string, details?: Partial<Omit<LogEntry, 'id' | 'timestamp' | 'level' | 'message'>>) => 
    log('DEBUG', message, details),
  info: (message: string, details?: Partial<Omit<LogEntry, 'id' | 'timestamp' | 'level' | 'message'>>) => 
    log('INFO', message, details),
  warn: (message: string, details?: Partial<Omit<LogEntry, 'id' | 'timestamp' | 'level' | 'message'>>) => 
    log('WARN', message, details),
  error: (message: string, errorObject?: Error | any, details?: Partial<Omit<LogEntry, 'id' | 'timestamp' | 'level' | 'message'>>) => {
    const errorDetails: Partial<Omit<LogEntry, 'id' | 'timestamp' | 'level' | 'message'>> = {
      ...details,
    };
    let processedMessage = message;
    if (errorObject) {
      errorDetails.stackTrace = errorObject?.stack;
      if (errorObject?.message && !message.includes(errorObject.message)) {
        processedMessage = `${message} - Error: ${errorObject.message}`;
      }
    }
    log('ERROR', processedMessage, errorDetails);
  },
  critical: (message: string, errorObject?: Error | any, details?: Partial<Omit<LogEntry, 'id' | 'timestamp' | 'level' | 'message'>>) => {
     const errorDetails: Partial<Omit<LogEntry, 'id' | 'timestamp' | 'level' | 'message'>> = {
      ...details,
    };
    let processedMessage = message;
    if (errorObject) {
      errorDetails.stackTrace = errorObject?.stack;
      if (errorObject?.message && !message.includes(errorObject.message)) {
        processedMessage = `${message} - Error: ${errorObject.message}`;
      }
    }
    log('CRITICAL', processedMessage, errorDetails);
  }
}; 